/**
 * Workspace Features.
 */
export interface WorkspaceConfig {
  billing: BillingConfig
  termsOfService: TermsOfServiceConfig
  privacyPolicy: PrivacyPolicyConfig
  jobs: JobConfig
  signup: SignupConfig
  email: EmailConfig
  calling: CallingConfig
  externalCustomer: ExternalCustomerConfig
  publicApi: PublicApiConfig
  combinedPricing: CombinedPricingConfig
  diy: DiyConfig
  jobDetailsConfig: JobDetailsConfig
  section7216: Section7216Config
  hubspotChat: HubspotChatConfig
  callCenter: CallCenterConfig
}

export interface JobDetailsConfig {}

export interface CombinedPricingConfig {
  jobScopePromptClientApproval: boolean
}

export interface EmailConfig {
  supportEmail: string
}

/**
 * Calling configuration
 */
export interface CallingConfig {
  enabled: boolean
}

/**
 * Configuration for Jobs
 */
export interface JobConfig {
  videoChat: boolean
  progressionFlow: boolean
  enableChatInInfoGathering: boolean
}

/**
 * Configuration for Billing.
 */
export interface BillingConfig {
  enabled: boolean
  coupons: CouponsConfig
  stripe_pk?: string
}

/**
 * Configuration for Coupons.
 */
export interface CouponsConfig {
  enabled: boolean
  referral: ReferralCouponConfig
}

/**
 * Configuration for Referral Coupons.
 */
export interface ReferralCouponConfig {
  enabled: boolean
  amount: number
}

/**
 * Configuration for SignUp.
 */
export interface SignupConfig {
  inviteOnly: boolean
  connections: AuthConnection[]
  title: string
  description: string
  logoutUrl?: string
  federatedLogout: boolean
  externalProfiles: boolean
}

export interface AuthConnection {
  type: string
  enabled: boolean
}

/**
 * Configuration for Terms of Service.
 */
export interface TermsOfServiceConfig {
  enabled: boolean
  customTermsEnabled: boolean
  showOnLogin: boolean
}

/**
 * Configuration for Privacy Policy.
 */
export interface PrivacyPolicyConfig {
  url: string
  enabled: boolean
}

/**
 * Configuration for External Customer.
 */
export interface ExternalCustomerConfig {
  allowRequestingMagicLinks: boolean
  enabled: boolean
}

/**
 * Configuration for Public API.
 */
export interface PublicApiConfig {
  connection: string
  magicLinkEnabled: boolean
}

/**
 * Configuration for DIY.
 */
export interface DiyConfig {
  /**
   * Whether or not to upsell consultations for DIY
   */
  upsellConsultations: boolean

  /**
   * The Legend ID of the legend that will be used to upsell consultations
   */
  consultationLegendId?: string
}

/**
 * Configuration for Section 7216.
 */
export interface Section7216Config {
  enabled: boolean
}

export interface HubspotChatConfig {
  enabled: boolean
}

export interface CallCenterConfig {
  enabled: boolean
  phoneNumber: string
  totalRequiredQuestions: number
  recommendAssistedOption: boolean
}

export const WorkspaceConfig = {
  default: {
    billing: {
      enabled: false,
      coupons: {
        enabled: false,
        referral: { enabled: false, amount: 0 },
      },
    },
    calling: {
      enabled: false,
    },
    jobs: {
      progressionFlow: false,
      videoChat: false,
      enableChatInInfoGathering: false,
    },
    signup: {
      externalProfiles: false,
      connections: [],
      description: 'Sign up for Taxfyle',
      federatedLogout: false,
      inviteOnly: false,
      title: 'Taxfyle',
    },
    privacyPolicy: {
      enabled: false,
      url: 'https://www.taxfyle.com/policy/privacy-policy-august-10-2021',
    },
    termsOfService: {
      enabled: false,
      showOnLogin: false,
      hideOnCustomerPortal: false,
      customTermsEnabled: false,
    },
    email: {
      supportEmail: 'support@taxfyle.com',
    },
    externalCustomer: {
      allowRequestingMagicLinks: false,
      enabled: false,
    },
    publicApi: {
      connection: '',
      magicLinkEnabled: false,
    },
    combinedPricing: {
      jobScopePromptClientApproval: false,
    },
    diy: {
      upsellConsultations: false,
    },
    jobDetailsConfig: {
      tabConfig: {
        overview: true,
      },
    },
    section7216: {
      enabled: false,
    },
    hubspotChat: {
      enabled: false,
    },
    callCenter: {
      enabled: false,
      phoneNumber: '',
      totalRequiredQuestions: 0,
      recommendAssistedOption: true,
    },
  } as WorkspaceConfig,
}
